import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { StickyTableScrollbar } from '@/components/ui/StickyTableScrollbar'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { TableSearchFilterEmptyPanel } from '@/components/ui/TableSearchFilterEmptyState'
import { useUserRole } from '@/hooks/useUserRole'
import { useIsMobile } from '@/hooks/useIsMobile'
import { PolicyDeclarationFilesPopup } from '@/features/reimbursements/components/PolicyDeclarationFilesPopup'
import { getPolicyDeclarationFiles } from '@/features/reimbursements/services/reimbursementsService'
import type { PolicyDeclarationFile } from '@/features/reimbursements/types'
import { openServeFileInNewTabFromGesture as openServeFileInNewTab } from '@/lib/utils/serveFileHelpers'
import { showApiError, showSuccess, showValidationMessage } from '@/lib/utils/notifications'
import {
  RESIDENT_PER_PAGE_OPTIONS,
  RESIDENT_POLICIES,
} from '../constants/residentConstants'
import { residentConfirmPresentation } from '../constants/residentDialogProps'
import { useResidentList } from '../hooks/useResidentList'
import { useResidentListColumns } from '../hooks/useResidentListColumns'
import {
  cancelDefaultClaim,
  fetchResidentDefault,
  saveResidentDefault,
  updateResidentPaid,
} from '../services/residentApi'
import type { ResidentListFilters, ResidentListRow } from '../types'
import { formatApplicantIdSegment } from '../utils/applicantIdFormat'
import { countResidentListActiveFilters } from '../utils/residentListFilterCount'
import { isActiveResidentRentalStatus } from '../utils/residentListStatusStyles'
import { readResidentListHandoffSearchFromLocation } from '../utils/residentListHandoffSearch'
import { ResidentBulkToolbar } from '../components/list/ResidentBulkToolbar'
import { ResidentColumnsMenu } from '../components/list/ResidentColumnsMenu'
import { ResidentFilters } from '../components/list/ResidentFilters'
import { ResidentGrid } from '../components/list/ResidentGrid'
import {
  ResidentPaymentInstructionsPanel,
} from '../components/list/ResidentPaymentInstructionsPanel'
import {
  RESIDENT_INSTRUCTIONS_PANEL_WIDTH_PX,
} from '../constants/instructionsLayout'
import { ResidentTable } from '../components/list/ResidentTable'
import { RentalStatusDialog } from '../components/dialogs/RentalStatusDialog'
import {
  StartReimbursementProcessDialog,
  type ReimbursementProcessChoice,
  type StartReimbursementProcessVariant,
} from '../components/dialogs/StartReimbursementProcessDialog'
import type { ResidentListConfirmState } from '../types'

type NotPaidBulkMode = StartReimbursementProcessVariant

type ReimbursementPromptState = null | {
  variant: NotPaidBulkMode
  residentName: string
  applicantId: string | number
  onSelect: (choice: ReimbursementProcessChoice) => void
  onCancel: () => void
}

export function ResidentListPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const role = useUserRole()
  const isMobile = useIsMobile()
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [instructionsOpen, setInstructionsOpen] = useState(false)
  const openInstructions = useCallback(() => setInstructionsOpen(true), [])
  const closeInstructions = useCallback(() => setInstructionsOpen(false), [])

  /**
   * Angular only reveals these instructions on demand (info-icon hover /
   * "Need additional guidance?"), never automatically on page load
   * (`resident-list.component.html:158-179`, `[hidden]="true"`).
   */

  /** Mirror Application checklist: shrink listing beside the fixed panel on desktop. */
  const [instructionsPushLayout, setInstructionsPushLayout] = useState(false)
  /** Application-list parity: sticky viewport scrollbar for wide table overflow. */
  const resultsScrollRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const update = () => {
      const width = window.innerWidth
      setInstructionsPushLayout(
        instructionsOpen && width >= RESIDENT_INSTRUCTIONS_PANEL_WIDTH_PX,
      )
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [instructionsOpen])

  const [searchInput, setSearchInput] = useState(() =>
    readResidentListHandoffSearchFromLocation(),
  )
  const [selectedRows, setSelectedRows] = useState<ResidentListRow[]>([])
  const [confirm, setConfirm] = useState<ResidentListConfirmState>(null)
  const [reimbursementPrompt, setReimbursementPrompt] = useState<ReimbursementPromptState>(null)
  const [multiEditOpen, setMultiEditOpen] = useState(false)
  const [bulkAction, setBulkAction] = useState<
    'PAID' | 'NOT_PAID_ONLY' | 'NOT_PAID_OPEN' | null
  >(null)
  const [policyFilesPopup, setPolicyFilesPopup] = useState<
    PolicyDeclarationFile[] | null
  >(null)
  const [accessConfirmationBusyApplicationId, setAccessConfirmationBusyApplicationId] =
    useState<string | number | null>(null)

  const {
    rows,
    loading,
    error,
    query,
    pagination,
    companyOptions,
    propertyOptions,
    salesOptions,
    statusLabelMaps,
    statusMappings,
    payStatusOptions,
    residentRentalStatusOptions,
    residentRentalProgressOptions,
    reimbursementTypeOptions,
    reimbursementRentalStatusOptions,
    reimbursementRentalProgressOptions,
    isCompanyLocked,
    isPropertyLocked,
    openExportInNewTab,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch,
    refetchFromStart,
    searchCompanies,
    searchProperties,
  } = useResidentList()

  const columns = useResidentListColumns()

  const applyFiltersRef = useRef(applyFilters)
  applyFiltersRef.current = applyFilters
  /** True only while the user is typing in the toolbar search (not when query syncs from session). */
  const userEditedSearchRef = useRef(false)
  const skipSearchSyncRef = useRef(false)

  useEffect(() => {
    if (skipSearchSyncRef.current) {
      skipSearchSyncRef.current = false
      return
    }
    if (userEditedSearchRef.current) return
    setSearchInput(query.q ?? '')
  }, [query.q])

  const handleSearchChange = useCallback((value: string) => {
    userEditedSearchRef.current = true
    setSearchInput(value)
  }, [])

  /**
   * The list loads with a default `orderBy` (Angular parity), but the header should
   * not look pre-sorted until the user actually clicks a column to sort.
   */
  const [sortInteracted, setSortInteracted] = useState(false)
  const handleSort = useCallback(
    (column: string) => {
      setSortInteracted(true)
      sort(column)
    },
    [sort],
  )

  const submitSearch = useCallback(() => {
    userEditedSearchRef.current = false
    skipSearchSyncRef.current = true
    applyFiltersRef.current({ q: searchInput })
  }, [searchInput])

  /**
   * Auto-apply the toolbar search shortly after the user stops typing, matching
   * the Application module's search box — no Enter/Done click required.
   */
  useEffect(() => {
    if (!userEditedSearchRef.current) return
    if (searchInput === (query.q ?? '')) return
    const timer = setTimeout(() => {
      submitSearch()
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput, query.q, submitSearch])

  /**
   * Angular `routeParams`: patch filters with `applicant_id` & `past_customer`, then drop them from URL.
   * Set the toolbar search immediately so the id is visible before the list request finishes.
   */
  useEffect(() => {
    const applicantId = searchParams.get('applicant_id')?.trim()
    const pastCustomer = searchParams.get('past_customer')?.trim()
    if (!applicantId && !pastCustomer) return
    if (applicantId) {
      setSearchInput(applicantId)
    }
    const next = new URLSearchParams(searchParams)
    next.delete('applicant_id')
    next.delete('past_customer')
    setSearchParams(next, { replace: true })
    const updates: Partial<ResidentListFilters> = {}
    if (applicantId && (query.q ?? '') !== applicantId) {
      updates.q = applicantId
    }
    if (pastCustomer && (query.past_customer ?? '') !== pastCustomer) {
      updates.past_customer = pastCustomer
    }

    if (Object.keys(updates).length > 0) {
      // Angular `routeParams` patches `q`; application handoff also sets past_customer=1.
      applyFiltersRef.current(updates)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, [])

  const selectedApplicantIds = useMemo(
    () => selectedRows.map((row) => row.applicant_id),
    [selectedRows],
  )
  
  // Clear selection when rows change (e.g. pagination, filters, sorting)
  useEffect(() => {
    setSelectedRows([])
  }, [rows])

  const canBulkSelect =
    role.hasPolicy([RESIDENT_POLICIES.listPaidInFull]) ||
    role.hasPolicy([RESIDENT_POLICIES.listNotPaid]) ||
    role.hasPolicy([RESIDENT_POLICIES.listMultiEdit])

  const activeFilterCount = useMemo(
    () =>
      countResidentListActiveFilters(query, {
        isCompanyLocked,
        isPropertyLocked,
      }),
    [isCompanyLocked, isPropertyLocked, query],
  )

  const toggleRow = useCallback(
    (resident: ResidentListRow, checked: boolean) => {
      setSelectedRows((prev) => {
        if (checked) {
          if (prev.some((row) => row.applicant_id === resident.applicant_id))
            return prev
          return [...prev, resident]
        }
        return prev.filter((row) => row.applicant_id !== resident.applicant_id)
      })
    },
    [],
  )

  const toggleAll = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setSelectedRows([])
        return
      }
      setSelectedRows(
        rows.filter((resident) =>
          isActiveResidentRentalStatus(resident.resident_rental_status) && resident.reimbursement_type === null,
        ),
      )
    },
    [rows],
  )

  const runPaidUpdate = useCallback(
    async (resident: ResidentListRow, paidType: string) => {
      await updateResidentPaid([resident.applicant_id], paidType)
      showSuccess('Action Updated Successfully')
    },
    [],
  )

  const processPaidQueue = useCallback(
    async (
      type: 'PAID' | 'NOT_PAID',
      queue = selectedRows,
      hasUpdates = false,
      notPaidMode: NotPaidBulkMode = 'not_paid_only',
    ) => {
      setBulkAction(
        type === 'PAID'
          ? 'PAID'
          : notPaidMode === 'open_reimbursement'
            ? 'NOT_PAID_OPEN'
            : 'NOT_PAID_ONLY',
      )
      const [resident, ...rest] = queue
      if (!resident) {
        setSelectedRows([])
        setBulkAction(null)
        // Only refetch when something actually changed — cancelling/skipping the whole
        // queue without any confirmed update shouldn't reload the list.
        if (hasUpdates) refetch()
        return
      }

      if (
        type === 'PAID' &&
        ['Eviction', 'Skipped'].includes(String(resident.reimbursement_type))
      ) {
        setConfirm({
          title: `Resident Paid: ${resident.first_name || ''} ${resident.last_name || ''} (${resident.applicant_id})`,
          titleClassName: '!text-base !font-bold !leading-snug',
          message:
            'Please confirm you want to set Paid for the selected resident for the current month.',
          primaryLabel: 'Confirm',
          secondaryLabel: 'Cancel',
          onSecondary: () => {
            setConfirm(null)
            void processPaidQueue(type, rest, hasUpdates)
          },
          onPrimary: async () => {
            setConfirm(null)
            try {
              const detail = await fetchResidentDefault(
                resident.fk_user_id,
                resident.application_id,
              )
              const rd = detail.resident_default
              const defaultId =
                rd?.fk_reimbursement_id ||
                rd?.fk_resident_default_id ||
                rd?.reimbursement_id ||
                (resident as { fk_reimbursement_id?: string | number }).fk_reimbursement_id ||
                (resident as { reimbursement_id?: string | number }).reimbursement_id
              if (defaultId) {
                setConfirm({
                  title: 'Rental Status Confirmation',
                  message:
                    "You've marked this resident as 'Paid In Full' but there is an active record open.\nDo you want to Cancel this?",
                  primaryLabel: 'Yes',
                  secondaryLabel: 'No',
                  onPrimary: async () => {
                    setConfirm(null)
                    try {
                      await cancelDefaultClaim(defaultId, {
                        default_claim: 'cancel_claim',
                        email_sendto: 'default_manager',
                        reason_note:
                          "This reimbursement has been cancelled by the Property when the resident was marked as 'Paid in Full'",
                        send_to: 'liberty',
                      })
                      await runPaidUpdate(resident, 'PAID')
                      await processPaidQueue(type, rest, true)
                    } catch (err) {
                      showApiError(err)
                      setBulkAction(null)
                    }
                  },
                  onSecondary: async () => {
                    setConfirm(null)
                    try {
                      await saveResidentDefault(resident.fk_user_id, {
                        email_sendto: 'default_manager',
                        application_id: resident.application_id,
                        message:
                          'The Property indicated that the resident has paid in full but did not cancel the reimbursement',
                        page: 'default',
                        send_to: 'liberty',
                      })
                      await runPaidUpdate(resident, 'PAID')
                      await processPaidQueue(type, rest, true)
                    } catch (err) {
                      showApiError(err)
                      setBulkAction(null)
                    }
                  },
                })
              } else {
                await runPaidUpdate(resident, 'PAID')
                await processPaidQueue(type, rest, true)
              }
            } catch (err) {
              showApiError(err)
              setBulkAction(null)
            }
          },
        })
        return
      }

      if (
        type === 'NOT_PAID' &&
        !['Eviction', 'Skipped'].includes(String(resident.reimbursement_type))
      ) {
        // Angular `showClaimProcess`: GET the default detail before opening the prompt
        // (validates the record exists; dialog copy is now fixed).
        try {
          await fetchResidentDefault(resident.fk_user_id, resident.application_id)
        } catch (err) {
          showApiError(err)
          setBulkAction(null)
          return
        }

        setReimbursementPrompt({
          variant: notPaidMode,
          residentName: `${resident.first_name || ''} ${resident.last_name || ''}`.trim(),
          applicantId: resident.applicant_id,
          // Angular `cancelbutton`: skip this resident and re-enter the queue for the next one.
          onCancel: () => {
            setReimbursementPrompt(null)
            void processPaidQueue(type, rest, hasUpdates, notPaidMode)
          },
          onSelect: async (choice: ReimbursementProcessChoice) => {
            setReimbursementPrompt(null)
            if (choice === 'EVICTION' || choice === 'SKIPPED') {
              try {
                await saveResidentDefault(resident.fk_user_id, {
                  reimbursement_type: choice,
                  rental_status: 'OPEN',
                  rental_progress: 'INFO_NEEDED',
                  page: 'default',
                  application_id: resident.application_id,
                })
                showSuccess('Data Record Updated Successfully')
                await runPaidUpdate(resident, 'NOT_PAID')
                // Angular opens /defaults/default-detail/...#default (Reimbursement Detail), not resident-detail.
                window.open(
                  `/defaults/default-detail/${formatApplicantIdSegment(resident.applicant_id)}/${resident.fk_user_id}#default`,
                  '_blank',
                )
                await processPaidQueue(type, rest, true, notPaidMode)
              } catch (err) {
                // Angular: appService.notify(error.error, 0) — e.g. "Application Record not found"
                showApiError(err)
                setBulkAction(null)
              }
            } else {
              try {
                await runPaidUpdate(resident, 'NOT_PAID')
                await processPaidQueue(type, rest, true, notPaidMode)
              } catch (err) {
                showApiError(err)
                setBulkAction(null)
              }
            }
          },
        })
        return
      }

      setConfirm({
        title: `Resident ${type === 'PAID' ? 'Paid' : 'Not Paid'}: ${resident.first_name || ''} ${resident.last_name || ''} (${resident.applicant_id})`,
        titleClassName: '!text-base !font-bold !leading-snug',
        message: `Please confirm you want to set ${type === 'PAID' ? 'Paid' : 'Not Paid'} for the selected resident for the current month.`,
        primaryLabel: 'Confirm',
        secondaryLabel: 'Cancel',
        onPrimary: async () => {
          setConfirm(null)
          await runPaidUpdate(resident, type)
          await processPaidQueue(type, rest, true)
        },
        onSecondary: () => {
          setConfirm(null)
          void processPaidQueue(type, rest, hasUpdates)
        },
      })
    },
    [refetch, runPaidUpdate, selectedRows],
  )

  const handleAccessConfirmation = useCallback(
    async (applicationId: string | number) => {
      setAccessConfirmationBusyApplicationId(applicationId)
      try {
        const resp = await getPolicyDeclarationFiles(applicationId)
        const files = resp.files ?? []
        if (files.length === 0) {
          showValidationMessage('No files found')
          return
        }
        if (files.length === 1) {
          const streamUrl = files[0]?.stream_url
          if (!streamUrl) {
            showValidationMessage('File URL not available')
            return
          }
          await openServeFileInNewTab(streamUrl)
        } else {
          setPolicyFilesPopup(files)
        }
      } catch (err) {
        showApiError(err)
      } finally {
        setAccessConfirmationBusyApplicationId(null)
      }
    },
    [],
  )

  const handleApplyFilters = useCallback(
    (filters: Partial<ResidentListFilters>) => {
      userEditedSearchRef.current = false
      applyFiltersRef.current({ ...filters, q: searchInput })
    },
    [searchInput],
  )

  return (
    <div
      className="min-h-screen bg-[#f8fafc] pb-12 font-sans text-slate-900"
      data-testid="resident-list-page"
    >
      <PageHeader title="RESIDENTS" />
      <div
        className="mx-auto flex w-full min-w-0 max-w-full flex-col gap-4 px-3 py-4 transition-[padding] duration-200 ease-out sm:gap-6 sm:px-6 sm:py-8"
        data-testid="resident-list-page-content"
        style={
          instructionsPushLayout
            ? { paddingRight: RESIDENT_INSTRUCTIONS_PANEL_WIDTH_PX }
            : undefined
        }
      >
        <ResidentBulkToolbar
          selectedCount={selectedRows.length}
          searchValue={searchInput}
          searchPlaceholder="Search residents..."
          searchLabel="Search residents"
          onSearchChange={handleSearchChange}
          onSearchSubmit={submitSearch}
          onOpenFilters={() => setIsFilterOpen(true)}
          activeFilterCount={activeFilterCount}
          testIdPrefix="resident-list"
          canMultiEdit={role.hasPolicy([RESIDENT_POLICIES.listMultiEdit])}
          canExport={
            rows.length > 0 && role.hasPolicy([RESIDENT_POLICIES.listExport])
          }
          canNotPaid={role.hasPolicy([RESIDENT_POLICIES.listNotPaid])}
          canPaid={role.hasPolicy([RESIDENT_POLICIES.listPaidInFull])}
          onExport={openExportInNewTab}
          onMultiEdit={() => setMultiEditOpen(true)}
          onNotPaidOnly={() => void processPaidQueue('NOT_PAID', selectedRows, false, 'not_paid_only')}
          onNotPaidOpenReimbursement={() =>
            void processPaidQueue('NOT_PAID', selectedRows, false, 'open_reimbursement')
          }
          onPaid={() => void processPaidQueue('PAID')}
          paidLoading={bulkAction === 'PAID'}
          notPaidOnlyLoading={bulkAction === 'NOT_PAID_ONLY'}
          notPaidOpenReimbursementLoading={bulkAction === 'NOT_PAID_OPEN'}
          columnsMenu={<ResidentColumnsMenu columns={columns} />}
        />
        <div
          className="flex flex-col gap-2 rounded-xl border border-[#b6d9f5] bg-[#e8f4fc] px-4 py-3 text-sm text-slate-700 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          data-testid="resident-list-rent-status-banner"
        >
          <p className="m-0 flex min-w-0 items-start gap-2 leading-snug">
            <span
              aria-hidden="true"
              className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1597ff] text-[11px] font-bold text-white"
            >
              i
            </span>
            <span>
              Update the monthly payment status for the active LR residents listed below. A
              reimbursement request can only be opened once a resident has been marked as not paid.
            </span>
          </p>
          <button
            type="button"
            data-testid="resident-list-how-does-this-work"
            className="shrink-0 self-end text-sm font-semibold text-[#7c3aed] underline-offset-2 hover:text-[#6d28d9] hover:underline sm:self-auto"
            onClick={openInstructions}
          >
            Need additional guidance?
          </button>
        </div>
        <ResidentFilters
          query={query}
          companyOptions={companyOptions}
          propertyOptions={propertyOptions}
          salesOptions={salesOptions}
          payStatusOptions={payStatusOptions}
          residentRentalStatusOptions={residentRentalStatusOptions}
          residentRentalProgressOptions={residentRentalProgressOptions}
          reimbursementTypeOptions={reimbursementTypeOptions}
          reimbursementRentalStatusOptions={reimbursementRentalStatusOptions}
          reimbursementRentalProgressOptions={reimbursementRentalProgressOptions}
          isCompanyLocked={isCompanyLocked}
          isPropertyLocked={isPropertyLocked}
          canUseAdvancedSearch={role.hasPolicy([
            RESIDENT_POLICIES.propertyConfigView,
          ])}
          isOpen={isFilterOpen}
          loading={loading}
          onApply={handleApplyFilters}
          onReset={() => {
            userEditedSearchRef.current = false
            skipSearchSyncRef.current = true
            resetFilters()
            setSearchInput('')
          }}
          onClose={() => setIsFilterOpen(false)}
          onCompanySearch={searchCompanies}
          onPropertySearch={searchProperties}
        />
        <div
          className="flex min-w-0 max-w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          data-testid="resident-list-results-card"
        >
          {selectedRows.length > 0 ? (
            <p
              className="m-0 border-b border-slate-100 px-3 pt-3 text-sm font-medium text-slate-700 sm:px-4"
              data-testid="resident-list-selection-count"
            >
              {selectedRows.length} resident{selectedRows.length === 1 ? '' : 's'} selected
            </p>
          ) : null}
          <ListPaginationBar
            placement="header"
            currentPage={pagination.current}
            lastPage={pagination.last}
            totalRecords={pagination.total}
            perPage={query.perPage}
            onPageChange={changePage}
            onPerPageChange={changePerPage}
            perPageOptions={RESIDENT_PER_PAGE_OPTIONS}
            summaryLayout="angular"
            testIdPrefix="resident-list"
            barDataTestId="resident-list-pagination-header-bar"
          />
          <div
            ref={resultsScrollRef}
            className="min-w-0 max-w-full overflow-x-auto touch-scroll"
            data-testid="resident-list-results"
          >
            {loading ? (
              <div
                className="flex items-center justify-center py-20 text-slate-600"
                data-testid="resident-list-loading"
              >
                <Loader2 className="mr-2 h-8 w-8 animate-spin text-[rgb(21_151_255)]" />{' '}
                Loading
              </div>
            ) : error ? (
              <div
                className="m-4 rounded-lg bg-red-50 p-4 text-center text-sm text-red-700 sm:p-8"
                data-testid="resident-list-error"
              >
                {error}
              </div>
            ) : rows.length === 0 ? (
              <TableSearchFilterEmptyPanel
                className="m-3 border-0 shadow-none sm:m-4"
                data-testid="resident-list-empty"
              />
            ) : isMobile ? (
              <ResidentGrid
                rows={rows}
                statusLabelMaps={statusLabelMaps}
                selectedApplicantIds={selectedApplicantIds}
                canCompanyView={role.hasPolicy([
                  RESIDENT_POLICIES.listCompanyView,
                ])}
                canPropertyView={role.hasPolicy([
                  RESIDENT_POLICIES.listPropertyView,
                ])}
                canSelect={canBulkSelect}
                canDetailView={role.hasPolicy([RESIDENT_POLICIES.detailView])}
                busyAccessConfirmationApplicationId={
                  accessConfirmationBusyApplicationId
                }
                onSelectRow={toggleRow}
                onAccessConfirmation={handleAccessConfirmation}
              />
            ) : (
              <ResidentTable
                rows={rows}
                statusLabelMaps={statusLabelMaps}
                query={query}
                selectedApplicantIds={selectedApplicantIds}
                canCompanyView={role.hasPolicy([
                  RESIDENT_POLICIES.listCompanyView,
                ])}
                canPropertyView={role.hasPolicy([
                  RESIDENT_POLICIES.listPropertyView,
                ])}
                canSelect={canBulkSelect}
                canDetailView={role.hasPolicy([RESIDENT_POLICIES.detailView])}
                busyAccessConfirmationApplicationId={
                  accessConfirmationBusyApplicationId
                }
                visibleColumnIds={columns.visibleColumnIds}
                orderedColumnIds={columns.columnOrderIds}
                horizontalScroll
                sortInteracted={sortInteracted}
                onSort={handleSort}
                onSelectAll={toggleAll}
                onSelectRow={toggleRow}
                onAccessConfirmation={handleAccessConfirmation}
              />
            )}
          </div>
          <ListPaginationBar
            placement="footer"
            currentPage={pagination.current}
            lastPage={pagination.last}
            totalRecords={pagination.total}
            perPage={query.perPage}
            onPageChange={changePage}
            onPerPageChange={changePerPage}
            perPageOptions={RESIDENT_PER_PAGE_OPTIONS}
            summaryLayout="angular"
            testIdPrefix="resident-list"
            barDataTestId="resident-list-pagination-footer-bar"
          />
          {!loading && !error && rows.length > 0 && !isMobile ? (
            <StickyTableScrollbar
              containerRef={resultsScrollRef}
              data-testid="resident-list-table-sticky-scrollbar"
            />
          ) : null}
        </div>
        {confirm ? (
          <ConfirmDialog
            isOpen
            title={confirm.title}
            titleClassName={confirm.titleClassName}
            message={confirm.message}
            primaryLabel={confirm.primaryLabel || 'Confirm'}
            secondaryLabel={confirm.secondaryLabel || 'Cancel'}
            neutralLabel={confirm.neutralLabel}
            onPrimary={confirm.onPrimary}
            onSecondary={confirm.onSecondary || (() => setConfirm(null))}
            onNeutral={confirm.onNeutral}
            onDismiss={() => {
              setConfirm(null)
              setBulkAction(null)
            }}
            {...residentConfirmPresentation}
            overlayDataTestId="resident-list-confirm-overlay"
            primaryButtonDataTestId="resident-list-confirm-primary-button"
            secondaryButtonDataTestId="resident-list-confirm-secondary-button"
            neutralButtonDataTestId="resident-list-confirm-neutral-button"
            closeButtonDataTestId="resident-list-confirm-close-button"
          />
        ) : null}
        <StartReimbursementProcessDialog
          isOpen={reimbursementPrompt !== null}
          variant={reimbursementPrompt?.variant ?? 'not_paid_only'}
          residentName={reimbursementPrompt?.residentName ?? ''}
          applicantId={reimbursementPrompt?.applicantId ?? ''}
          onClose={() => reimbursementPrompt?.onCancel()}
          onSelect={(choice) => reimbursementPrompt?.onSelect(choice)}
        />
        <RentalStatusDialog
          isOpen={multiEditOpen}
          selectedApplicantIds={selectedApplicantIds}
          onClose={() => setMultiEditOpen(false)}
          statusMappings={statusMappings}
          onUpdated={() => {
            setSelectedRows([])
            // Angular `triggerFilter()`: Multi-Edit's refresh resets to page 1.
            refetchFromStart()
          }}
        />
        <PolicyDeclarationFilesPopup
          isOpen={policyFilesPopup !== null}
          onClose={() => setPolicyFilesPopup(null)}
          files={policyFilesPopup ?? []}
        />
        <ResidentPaymentInstructionsPanel
          isOpen={instructionsOpen}
          onClose={closeInstructions}
        />
      </div>
    </div>
  )
}
